import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import api from "@/lib/api";
import { format, differenceInBusinessDays, addDays, isWeekend } from "date-fns";
import { es } from "date-fns/locale";
import { CalendarDays, Send, AlertCircle, Loader2, Info, CheckCircle, Link, Copy } from "lucide-react";
import { cn } from "@/lib/utils";

const NuevaSolicitudPage = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fechaInicio, setFechaInicio] = useState(null);
  const [fechaFin, setFechaFin] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [diasCalculados, setDiasCalculados] = useState(0);
  const [isHalfDay, setIsHalfDay] = useState(false);
  const [incluirFinesDeSemana, setIncluirFinesDeSemana] = useState(false);
  const [error, setError] = useState("");
  const [successData, setSuccessData] = useState(null);
  const [holidays, setHolidays] = useState([]);

  // Fetch holidays for the user's country
  useEffect(() => {
    const fetchUserHolidays = async () => {
      try {
        const userPais = user?.pais || "Guatemala";
        const response = await api.getHolidays(userPais);
        const dates = response.data.map(h => h.fecha);
        setHolidays(dates);
      } catch (err) {
        console.error("Error loading holidays:", err);
      }
    };
    if (user) {
      fetchUserHolidays();
    }
  }, [user]);

  // Calculate business days when dates change, excluding holidays
  useEffect(() => {
    if (fechaInicio && fechaFin) {
      if (isHalfDay && format(fechaInicio, "yyyy-MM-dd") === format(fechaFin, "yyyy-MM-dd")) {
        setDiasCalculados(0.5);
        return;
      }

      let count = 0;
      let current = new Date(fechaInicio);
      const end = new Date(fechaFin);
      
      while (current <= end) {
        const dateStr = format(current, "yyyy-MM-dd");
        const isHoli = holidays.includes(dateStr);
        if (!isHoli) {
          const dayOfWeek = current.getDay(); // 0 is Sunday, 6 is Saturday
          if (dayOfWeek === 0) {
            // Sunday is always 0.0 days
          } else if (dayOfWeek === 6) {
            // Saturday is 0.5 days if incluirFinesDeSemana is enabled
            if (incluirFinesDeSemana) {
              count += 0.5;
            }
          } else {
            // Weekday is 1.0 day
            count += 1.0;
          }
        }
        current = addDays(current, 1);
      }
      
      setDiasCalculados(count);
    } else {
      setDiasCalculados(0);
    }
  }, [fechaInicio, fechaFin, isHalfDay, incluirFinesDeSemana, holidays]);

  // Reset half day if dates change and are not the same
  useEffect(() => {
    if (fechaInicio && fechaFin && format(fechaInicio, "yyyy-MM-dd") !== format(fechaFin, "yyyy-MM-dd")) {
      setIsHalfDay(false);
    }
  }, [fechaInicio, fechaFin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!fechaInicio || !fechaFin) {
      setError("Por favor seleccione las fechas de inicio y fin");
      return;
    }

    if (fechaFin < fechaInicio) {
      setError("La fecha de fin debe ser posterior a la fecha de inicio");
      return;
    }

    if (diasCalculados > user?.dias_disponibles) {
      setError(`No tiene suficientes días disponibles. Disponible: ${user?.dias_disponibles} días`);
      return;
    }

    if (!motivo.trim()) {
      setError("Por favor ingrese el motivo de la solicitud");
      return;
    }

    setLoading(true);

    try {
      const requestData = {
        fecha_inicio: format(fechaInicio, "yyyy-MM-dd"),
        fecha_fin: format(fechaFin, "yyyy-MM-dd"),
        motivo: motivo.trim(),
        dias_solicitados: diasCalculados,
      };

      const response = await api.createVacationRequest(requestData);
      await refreshUser();
      
      toast.success("Solicitud enviada", {
        description: "Tu solicitud ha sido enviada al supervisor para aprobación",
      });
      
      setSuccessData({
        fecha_inicio: format(fechaInicio, "dd/MM/yyyy"),
        fecha_fin: format(fechaFin, "dd/MM/yyyy"),
        dias: diasCalculados
      });
      
      // We don't navigate immediately to let the user see the success screen
      // navigate("/historial");
    } catch (err) {
      console.error("Error creating request:", err);
      setError(err.response?.data?.detail || "Error al crear la solicitud");
    } finally {
      setLoading(false);
    }
  };

  // Normalizar minDate al inicio del día para evitar desfases de horas
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const minDate = today; 

  if (successData) {
    const portalUrl = `${window.location.origin}/aprobaciones`;
    const shareText = `Hola, he solicitado mis vacaciones del ${successData.fecha_inicio} al ${successData.fecha_fin} (${successData.dias} días). Por favor, revisa mi solicitud en el portal: ${portalUrl}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;

    return (
      <div className="max-w-md mx-auto space-y-6 animate-fadeIn mt-10">
        <Card className="border-emerald-200">
          <CardContent className="p-8 flex flex-col items-center text-center space-y-6">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-800">Solicitud Enviada</h2>
              <p className="text-slate-500 mt-2">
                Tu solicitud ha sido enviada al supervisor para su aprobación.
              </p>
            </div>

            <div className="w-full space-y-3 pt-4">
              <Button 
                className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white flex items-center justify-center gap-2"
                onClick={() => window.open(whatsappUrl, "_blank")}
              >
                <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path></svg>
                Compartir por WhatsApp
              </Button>
              
              <Button 
                variant="outline"
                className="w-full flex items-center justify-center gap-2"
                onClick={() => {
                  navigator.clipboard.writeText(shareText);
                  toast.success("Mensaje copiado", { description: "Mensaje y enlace copiados al portapapeles" });
                }}
              >
                <Copy className="w-4 h-4" />
                Copiar Mensaje
              </Button>

              <Button 
                variant="ghost"
                className="w-full mt-2"
                onClick={() => navigate("/historial")}
              >
                Ir a mi historial
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fadeIn" data-testid="nueva-solicitud-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-800 tracking-tight">
          Nueva Solicitud de Vacaciones
        </h1>
        <p className="text-slate-500 mt-1">
          Completa el formulario para solicitar días de vacaciones
        </p>
      </div>

      {/* Balance Info */}
      <Card className="border-sky-200 bg-sky-50">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <Info className="w-5 h-5 text-sky-600" />
            <div>
              <p className="text-sky-800 font-medium">
                Tienes <span className="text-xl font-bold">{user?.dias_disponibles || 0}</span> días disponibles
              </p>
              <p className="text-sky-600 text-sm">
                Acumulados: {user?.dias_acumulados || 0} | Usados: {user?.dias_usados || 0}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-100">
        <CardHeader>
          <CardTitle>Detalles de la Solicitud</CardTitle>
          <CardDescription>
            Selecciona las fechas y describe el motivo de tu solicitud
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <Alert variant="destructive" className="bg-red-50 border-red-200">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Date Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Fecha de Inicio</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal h-12",
                        !fechaInicio && "text-slate-500"
                      )}
                      data-testid="fecha-inicio-btn"
                    >
                      <CalendarDays className="mr-2 h-4 w-4" />
                      {fechaInicio ? (
                        format(fechaInicio, "PPP", { locale: es })
                      ) : (
                        "Seleccionar fecha"
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={fechaInicio}
                      onSelect={setFechaInicio}
                      disabled={(date) => 
                        date < minDate || 
                        date.getDay() === 0 || // Sunday always disabled
                        (!incluirFinesDeSemana && date.getDay() === 6) || // Saturday disabled unless incluirFinesDeSemana is enabled
                        holidays.includes(format(date, "yyyy-MM-dd"))
                      }
                      initialFocus
                      locale={es}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>Fecha de Fin</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal h-12",
                        !fechaFin && "text-slate-500"
                      )}
                      data-testid="fecha-fin-btn"
                    >
                      <CalendarDays className="mr-2 h-4 w-4" />
                      {fechaFin ? (
                        format(fechaFin, "PPP", { locale: es })
                      ) : (
                        "Seleccionar fecha"
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={fechaFin}
                      onSelect={setFechaFin}
                      disabled={(date) => 
                        date < minDate || 
                        (fechaInicio && date < fechaInicio) ||
                        date.getDay() === 0 || // Sunday always disabled
                        (!incluirFinesDeSemana && date.getDay() === 6) || // Saturday disabled unless incluirFinesDeSemana is enabled
                        holidays.includes(format(date, "yyyy-MM-dd"))
                      }
                      initialFocus
                      locale={es}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Half Day & Weekend Toggles */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {fechaInicio && fechaFin && format(fechaInicio, "yyyy-MM-dd") === format(fechaFin, "yyyy-MM-dd") && (
                <div className="flex items-center space-x-2 py-2 px-4 bg-slate-50 rounded-lg border border-slate-200">
                  <Switch 
                    id="half-day" 
                    checked={isHalfDay} 
                    onCheckedChange={setIsHalfDay}
                    className="data-[state=checked]:bg-orange-500"
                  />
                  <Label htmlFor="half-day" className="cursor-pointer font-medium text-slate-700">
                    Solicitar Medio Día (0.5 días)
                  </Label>
                </div>
              )}

              <div className="flex items-center space-x-2 py-2 px-4 bg-slate-50 rounded-lg border border-slate-200">
                <Switch 
                  id="include-weekends" 
                  checked={incluirFinesDeSemana} 
                  onCheckedChange={setIncluirFinesDeSemana}
                  className="data-[state=checked]:bg-indigo-500"
                />
                <Label htmlFor="include-weekends" className="cursor-pointer font-medium text-slate-700">
                  Incluir fines de semana
                </Label>
              </div>
            </div>

            {/* Days Calculation */}
            {diasCalculados > 0 && (
              <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                <div className="flex items-center justify-between">
                  <span className="text-orange-800">
                    {incluirFinesDeSemana ? "Días totales solicitados:" : "Días hábiles solicitados:"}
                  </span>
                  <span className="text-2xl font-bold text-orange-600">{diasCalculados}</span>
                </div>
                {diasCalculados > user?.dias_disponibles && (
                  <p className="text-red-600 text-sm mt-2">
                    ⚠️ Excede los días disponibles ({user?.dias_disponibles})
                  </p>
                )}
              </div>
            )}

            {/* Reason */}
            <div className="space-y-2">
              <Label htmlFor="motivo">Motivo de la Solicitud</Label>
              <Textarea
                id="motivo"
                placeholder="Describe brevemente el motivo de tu solicitud de vacaciones..."
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                rows={4}
                className="resize-none"
                data-testid="motivo-input"
              />
            </div>

            {/* Supervisor Info */}
            {user?.supervisor_id && (
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">
                  Tu solicitud será enviada a tu supervisor directo para aprobación.
                </p>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                disabled={loading || diasCalculados === 0 || diasCalculados > user?.dias_disponibles}
                data-testid="submit-request-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Enviar Solicitud
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default NuevaSolicitudPage;
